import { get } from "../api.js";

export type StaffSlotState =
  | "available"
  | "historical"
  | "within_notice_period"
  | "blocked_time"
  | "outside_working_hours"
  | "unavailable";

export interface StaffSlotCheck {
  provider_id: number;
  service_id: number;
  starts_at: string;
  state: StaffSlotState;
  working_hours_override_available: boolean;
  schedule_override_available?: boolean;
  message: string | null;
}

/**
 * Exact-time staff slot classification.
 *
 * Public Amelia availability deliberately omits a tutor's closed hours and is
 * forward-looking, so a missing time is not proof of a conflict. WMF-SIte owns
 * the distinction between historical, notice-period, blocked, out-of-hours,
 * and genuinely unavailable states; this read exposes it for one instant.
 * The write side carries an explicit `override_schedule_policy` flag (with a
 * compatibility alias for older clients) and is revalidated server-side
 * before Amelia changes.
 */
export const staffWorkingHours = {
  check: async (input: {
    providerId: number;
    serviceId: number;
    startsAt: string;
  }): Promise<StaffSlotCheck> => {
    const result = await get<StaffSlotCheck>("/staff-api/api/v1/availability/staff-slot", {
      provider_id: String(input.providerId),
      service_id: String(input.serviceId),
      starts_at: input.startsAt,
    });

    // FastAPI may serialize the same instant as `Z` or `+00:00`. Fail closed if
    // the answer is genuinely about another target, then preserve the caller's
    // exact spelling so the view can correlate this check with its selected slot
    // without turning timestamp formatting into scheduling policy.
    if (Date.parse(result.starts_at) !== Date.parse(input.startsAt)) {
      throw new Error("The booking system answered about a different lesson time.");
    }

    const overrideState = [
      "historical",
      "within_notice_period",
      "blocked_time",
      "outside_working_hours",
    ].includes(result.state);
    return {
      ...result,
      starts_at: input.startsAt,
      // An inconsistent response must never make a hard-unavailable state look
      // overrideable. The state is the authoritative classification.
      working_hours_override_available:
        overrideState && result.working_hours_override_available,
      schedule_override_available:
        overrideState &&
        (result.schedule_override_available ?? result.working_hours_override_available),
    };
  },
};
